class CartThresholdOffers extends HTMLElement {
  constructor() {
    super();
    this.DEBUG = false;
    this.OFFER_PROPERTY_KEY = '_ThresholdOffer';
    this.offers = [];
    this.currentCart = null;
    this.currentCartTotal = 0;
    this.activeManualOfferId = null;
    this.isManualAddInProgress = false;

    this.isProcessing = false;
    this.lastProcessAt = 0;
    this.THROTTLE_DELAY = 300;

    this.handleCartUpdate = this.handleCartUpdate.bind(this);
    this.handleManualAdd = this.handleManualAdd.bind(this);
    this.throttledHandler = this.createThrottledHandler();
  }

  connectedCallback() {
    this.parseConfig();
    if (!this.offers.length) return;

    this.cacheDom();
    this.bindEvents();
    this.initializeWithCurrentCart();
  }

  disconnectedCallback() {
    document.removeEventListener('theme:cart:change', this.throttledHandler);
    if (this.addButton) this.addButton.removeEventListener('click', this.handleManualAdd);
  }

  cacheDom() {
    this.container = this.querySelector('[data-threshold-offer-container]');
    this.messageEl = this.querySelector('[data-threshold-message]');
    this.progressEl = this.querySelector('[data-threshold-progress]');
    this.progressFillEl = this.querySelector('[data-threshold-progress-fill]');
    this.groupedEl = this.querySelector('[data-threshold-grouped]');
    this.groupedOffersEl = this.querySelector('[data-threshold-grouped-offers]');
    this.groupedThresholdsEl = this.querySelector('[data-threshold-grouped-thresholds]');

    this.selectorEl = this.querySelector('[data-product-selector]');
    this.selectorTitleEl = this.querySelector('[data-selector-title]');
    this.selectorOptionsEl = this.querySelector('[data-product-options]');
    this.addButton = this.querySelector('[data-add-selected-product]');
    this.checkoutMessageEl =
      this.closest('cart-drawer')?.querySelector('[data-threshold-checkout-message]') ||
      document.querySelector('[data-threshold-checkout-message]');
  }

  renderCheckoutMessage(messageHtml, isSuccess, isVisible) {
    if (!this.checkoutMessageEl) return;
    if (!isVisible || !messageHtml) {
      this.checkoutMessageEl.style.display = 'none';
      this.checkoutMessageEl.classList.remove('cart-threshold-checkout-message--success');
      this.checkoutMessageEl.innerHTML = '';
      return;
    }

    this.checkoutMessageEl.innerHTML = messageHtml;
    this.checkoutMessageEl.style.display = 'block';
    this.checkoutMessageEl.classList.toggle('cart-threshold-checkout-message--success', Boolean(isSuccess));
  }

  getCheckoutOnlyPrePostMessage(offer, thresholdReached, cartTotalCents, topUsesSuccessMessage = false) {
    if (!offer) return '';
    if (topUsesSuccessMessage) return '';

    if (thresholdReached) {
      // Checkout-area message should only show post message, never success message.
      return offer.post_message || '';
    }

    const thresholdCents = Math.round(offer.cart_total_threshold * 100);
    const remaining = Math.max(thresholdCents - cartTotalCents, 0);
    const formatted = window.theme?.formatMoney
      ? window.theme.formatMoney(remaining, theme.moneyFormat)
      : `${(remaining / 100).toFixed(2)}`;

    return (offer.pre_message || '').replace('[amount]', formatted);
  }

  bindEvents() {
    document.addEventListener('theme:cart:change', this.throttledHandler);
    if (this.addButton) this.addButton.addEventListener('click', this.handleManualAdd);
  }

  parseConfig() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) return;

    try {
      const parsed = JSON.parse(script.textContent || '{}');
      const offers = Array.isArray(parsed.offers) ? parsed.offers : [];

      this.offers = offers
        .map((offer) => this.normalizeOffer(offer))
        .filter((offer) => offer && offer.cart_total_threshold > 0)
        .sort((a, b) => a.cart_total_threshold - b.cart_total_threshold);
    } catch (error) {
      console.error('[ThresholdOffers] parse error', error);
      this.offers = [];
    }
  }

  normalizeOffer(raw) {
    if (!raw || typeof raw !== 'object') return null;

    const normalizeMode = (input) => {
      let mode = input;
      if (Array.isArray(mode)) mode = mode[0];
      mode = String(mode || '').trim().toLowerCase();
      if (mode === 'manual' || mode === 'manual_selection') return 'manual_selection';
      if (mode === 'silent' || mode === 'silent_auto') return 'silent_auto';
      if (mode === 'auto' || mode === 'auto_with_message') return 'auto_with_message';
      return 'auto_with_message';
    };

    const options = Array.isArray(raw.product_options)
      ? raw.product_options
          .map((option) => ({
            variant_id: Number(option.variant_id || 0),
            product_title: String(option.product_title || ''),
            variant_title: String(option.variant_title || ''),
            image: option.image ? String(option.image) : ''
          }))
          .filter((option) => option.variant_id > 0)
      : [];

    const offerId = String(raw.offer_id || raw.property_name || '');
    const thresholdNumber = Number(raw.cart_total_threshold || 0);

    return {
      offer_id: offerId,
      title: raw.title ? String(raw.title) : '',
      offer_mode: normalizeMode(raw.offer_mode),
      cart_total_threshold: thresholdNumber,
      show_messages: raw.show_messages === true || raw.show_messages === 'true',
      show_progress_bar: raw.show_progress_bar === true || raw.show_progress_bar === 'true',
      pre_message: raw.pre_message ? String(raw.pre_message) : '',
      post_message: raw.post_message ? String(raw.post_message) : '',
      success_message: raw.success_message ? String(raw.success_message) : '',
      icon: raw.icon ? String(raw.icon) : '',
      free_products: Array.isArray(raw.free_products)
        ? raw.free_products.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
        : [],
      property_name: String(raw.property_name || offerId || `threshold_${thresholdNumber}`),
      selector_title: raw.selector_title ? String(raw.selector_title) : 'Select your free gift:',
      add_button_text: raw.add_button_text ? String(raw.add_button_text) : 'Add Free Gift',
      product_options: options
    };
  }

  createThrottledHandler() {
    let timer = null;
    return (event) => {
      const now = Date.now();
      const since = now - this.lastProcessAt;
      if (since >= this.THROTTLE_DELAY && !this.isProcessing) {
        this.lastProcessAt = now;
        this.handleCartUpdate(event);
        return;
      }
      clearTimeout(timer);
      timer = setTimeout(() => {
        this.lastProcessAt = Date.now();
        this.handleCartUpdate(event);
      }, this.THROTTLE_DELAY);
    };
  }

  async initializeWithCurrentCart() {
    const cart = await window.CartOfferUtils.getCartData(null, false);
    if (cart) await this.process(cart);
  }

  async handleCartUpdate(event) {
    if (event?.detail?.source === 'threshold') return;
    const hasEventCart = Boolean(event?.detail?.cart && Array.isArray(event.detail.cart.items));
    if (this.DEBUG) console.log('[ThresholdOffers] handleCartUpdate source', {
      hasEventCart,
      eventCartItemCount: hasEventCart ? event.detail.cart.items.length : null,
      eventCartTotalPrice: hasEventCart ? event.detail.cart.total_price : null,
      eventCartItemsSubtotal: hasEventCart ? event.detail.cart.items_subtotal_price : null
    });

    let cart = null;
    if (hasEventCart) cart = event.detail.cart;
    // The theme's own theme:cart:change carries only cartCount (theme.dev.js),
    // so this is the path taken after every quantity change. It must read a
    // fresh cart: the shared cache has an 800ms TTL, and deciding "is the gift
    // missing?" from a stale snapshot adds a second gift, which Shopify merges
    // into the same line.
    if (!cart) cart = await window.CartOfferUtils.getCartData(null, true);

    if (!cart || !Array.isArray(cart.items)) {
      cart = await window.CartOfferUtils.getCartData(null, true);
    }
    if (!cart) return;

    await this.process(cart);
  }

  calculateCartTotal(cart) {
    if (!cart?.items) return 0;

    const normalizeCents = (value) => {
      if (value == null || value === '') return 0;

      if (typeof value === 'number') {
        if (!Number.isFinite(value)) return 0;
        return Math.round(value);
      }

      const cleaned = String(value).replace(/[^0-9.-]/g, '');
      if (!cleaned) return 0;
      const parsed = Number(cleaned);
      if (!Number.isFinite(parsed)) return 0;

      // If the value looks like a decimal currency amount, convert to cents.
      if (cleaned.includes('.')) return Math.round(parsed * 100);
      return Math.round(parsed);
    };

    const getLineTotal = (item) => {
      const direct = normalizeCents(item?.final_line_price ?? item?.line_price ?? item?.original_line_price);
      if (direct > 0) return direct;

      const unit = normalizeCents(item?.final_price ?? item?.price ?? 0);
      const qty = Number(item?.quantity ?? 1);
      return Math.max(unit, 0) * Math.max(qty, 0);
    };

    const isOfferInjectedItem = (item) => {
      if (!item?.properties) return false;
      return Boolean(item.properties._MultipleOfferTier || item.properties._Offer || item.properties[this.OFFER_PROPERTY_KEY]);
    };

    const totalsByPriority = [
      normalizeCents(cart?.total_price),
      normalizeCents(cart?.items_subtotal_price),
      normalizeCents(cart?.subtotal_price),
      normalizeCents(cart?.original_total_price)
    ];
    const baseTotal = totalsByPriority.find((value) => Number.isFinite(value) && value > 0) || 0;

    if (this.DEBUG) console.log('[ThresholdOffers] calculateCartTotal cart fields', {
      item_count: cart?.item_count,
      total_price: cart?.total_price,
      items_subtotal_price: cart?.items_subtotal_price,
      currency: cart?.currency,
      itemCountFromItems: Array.isArray(cart?.items) ? cart.items.length : 0
    });

    const excludedTotal = cart.items.reduce((sum, item, index) => {
      const lineTotal = getLineTotal(item);
      const exclusionReason = [];

      // Gift cards do not count toward a GWP threshold: buying store credit is
      // not spend on eligible products. Checked before the `!item.properties`
      // early return below, because gift card lines carry no properties.
      if (item.gift_card === true) {
        exclusionReason.push('gift_card');
        if (this.DEBUG) console.log('[ThresholdOffers] line', index, {
          variant_id: item.variant_id,
          title: item.title,
          computedLineTotal: lineTotal,
          excluded: true,
          exclusionReason
        });
        return sum + lineTotal;
      }

      if (item.product_type === 'OPTIONS_HIDDEN_PRODUCT') {
        exclusionReason.push('hidden_product');
        if (this.DEBUG) console.log('[ThresholdOffers] line', index, {
          variant_id: item.variant_id,
          key: item.key,
          quantity: item.quantity,
          final_line_price: item.final_line_price,
          line_price: item.line_price,
          original_line_price: item.original_line_price,
          final_price: item.final_price,
          computedLineTotal: lineTotal,
          properties: item.properties || null,
          excluded: true,
          exclusionReason
        });
        return sum + lineTotal;
      }

      if (!item.properties) {
        if (this.DEBUG) console.log('[ThresholdOffers] line', index, {
          variant_id: item.variant_id,
          key: item.key,
          quantity: item.quantity,
          final_line_price: item.final_line_price,
          line_price: item.line_price,
          original_line_price: item.original_line_price,
          final_price: item.final_price,
          computedLineTotal: lineTotal,
          properties: null,
          excluded: false
        });
        return sum;
      }
      if (isOfferInjectedItem(item)) {
        exclusionReason.push('offer_injected');
        if (item.properties._MultipleOfferTier) exclusionReason.push('_MultipleOfferTier');
        if (item.properties._Offer) exclusionReason.push('_Offer');
        if (item.properties[this.OFFER_PROPERTY_KEY]) exclusionReason.push(this.OFFER_PROPERTY_KEY);
        if (this.DEBUG) console.log('[ThresholdOffers] line', index, {
          variant_id: item.variant_id,
          key: item.key,
          quantity: item.quantity,
          final_line_price: item.final_line_price,
          line_price: item.line_price,
          original_line_price: item.original_line_price,
          final_price: item.final_price,
          computedLineTotal: lineTotal,
          properties: item.properties || null,
          excluded: true,
          exclusionReason
        });
        return sum + lineTotal;
      }

      if (this.DEBUG) console.log('[ThresholdOffers] line', index, {
        variant_id: item.variant_id,
        key: item.key,
        quantity: item.quantity,
        final_line_price: item.final_line_price,
        line_price: item.line_price,
        original_line_price: item.original_line_price,
        final_price: item.final_price,
        computedLineTotal: lineTotal,
        properties: item.properties || null,
        excluded: false
      });

      return sum;
    }, 0);

    if (Number.isFinite(baseTotal) && baseTotal > 0) {
      const resolved = Math.max(baseTotal - excludedTotal, 0);
      if (this.DEBUG) console.log('[ThresholdOffers][build-2026-04-13-3] total resolution (base path)', {
        totalsByPriority,
        baseTotal,
        excludedTotal,
        resolved
      });
      return resolved;
    }

    const resolvedFromLines = cart.items.reduce((sum, item) => {
      const lineTotal = getLineTotal(item);

      if (item.product_type === 'OPTIONS_HIDDEN_PRODUCT') return sum;
      if (!item.properties) return sum + lineTotal;
      if (isOfferInjectedItem(item)) return sum;

      return sum + lineTotal;
    }, 0);
    if (this.DEBUG) console.log('[ThresholdOffers][build-2026-04-13-3] total resolution (line fallback path)', {
      resolvedFromLines
    });
    return resolvedFromLines;
  }

  getOfferItems(cart, offer) {
    if (!cart?.items) return [];
    return cart.items.filter((item) => item.properties && item.properties[this.OFFER_PROPERTY_KEY] === offer.property_name);
  }

  async addProductWithRetry(variantId, properties, attempts = 4) {
    let i = 0;
    while (i < attempts) {
      i += 1;
      const result = await window.CartOfferUtils.addProduct(variantId, 1, properties);
      if (result.success) return result;
      if (result.reason !== 'processing') return result;
      await new Promise((resolve) => setTimeout(resolve, 120 * i));
    }
    return { success: false, reason: 'processing' };
  }

  // Removes every offer line in a single /cart/update.js request. One request
  // per line meant a full round trip and cart-refresh cycle per gift when a
  // customer dropped below the threshold.
  async removeOfferItems(offerItems) {
    const updates = {};
    offerItems.forEach((item) => {
      if (item?.key) updates[item.key] = 0;
    });
    await this.applyQuantityUpdates(updates);
  }

  // Sets each line key to an exact quantity (0 removes it) in one request.
  async applyQuantityUpdates(updates) {
    if (!updates || !Object.keys(updates).length) return;

    try {
      await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });
    } catch (error) {
      console.error('[ThresholdOffers] remove error', error);
    }
  }

  async syncOfferProducts(cart, statuses) {
    let mutated = false;

    // Decide everything first, then issue at most one remove and one add
    // request. Doing it per offer meant a round trip and cart-refresh cycle
    // per offer when several thresholds changed state at once.
    const itemsToRemove = [];
    const itemsToAdd = [];
    const fallbackAdds = [];
    const quantityUpdates = {};

    for (const status of statuses) {
      const { offer, thresholdReached, offerItems } = status;
      const mode = offer.offer_mode;

      if (!thresholdReached) {
        if (offerItems.length > 0) {
          itemsToRemove.push(...offerItems);
        }
        continue;
      }

      if (mode === 'manual_selection') {
        continue;
      }

      // A gift is one unit. Extra units accumulate when a burst of cart changes
      // is evaluated against a snapshot that predates an earlier add - Shopify
      // merges same-variant-same-property adds into a single line, and only the
      // first unit is covered by the discount, so the rest get charged. Trim
      // back to one so carts already in that state heal themselves.
      offer.free_products.forEach((variantId) => {
        let allowed = 1;
        offerItems
          .filter((item) => Number(item.variant_id) === Number(variantId))
          .forEach((row) => {
            if (!row.key) return;
            const qty = Number(row.quantity || 0);
            const keep = Math.min(qty, allowed);
            allowed -= keep;
            if (keep !== qty) quantityUpdates[row.key] = keep;
          });
      });

      const existingVariantIds = new Set(
        offerItems.map((item) => Number(item.variant_id)).filter((id) => Number.isFinite(id) && id > 0)
      );
      const missing = offer.free_products.filter((id) => !existingVariantIds.has(Number(id)));
      if (!missing.length) continue;

      missing.forEach((variantId) => {
        itemsToAdd.push({
          variantId: Number(variantId),
          quantity: 1,
          properties: { [this.OFFER_PROPERTY_KEY]: offer.property_name }
        });
        fallbackAdds.push({ variantId, propertyName: offer.property_name });
      });
    }

    itemsToRemove.forEach((item) => {
      if (item?.key) quantityUpdates[item.key] = 0;
    });

    if (Object.keys(quantityUpdates).length > 0) {
      await this.applyQuantityUpdates(quantityUpdates);
      mutated = true;
    }

    if (itemsToAdd.length > 0) {
      if (window.CartOfferUtils && typeof window.CartOfferUtils.addProducts === 'function') {
        const result = await window.CartOfferUtils.addProducts(itemsToAdd);
        if (result.success) mutated = true;
      } else {
        for (const add of fallbackAdds) {
          const result = await this.addProductWithRetry(add.variantId, {
            [this.OFFER_PROPERTY_KEY]: add.propertyName
          });
          if (result.success) mutated = true;
        }
      }
    }

    return mutated;
  }

  formatMessage(offer, message) {
    if (!message) return '';
    if (!offer.icon) return message;
    return `${message}`;
  }

  renderGrouped(statuses, cartTotal) {
    if (!this.groupedOffersEl || !this.groupedThresholdsEl) return;

    const groupedStatuses = statuses.filter((status) => status.offer.show_progress_bar === true);
    if (!groupedStatuses.length) {
      if (this.groupedEl) this.groupedEl.style.display = 'none';
      this.renderSequential(this.getSequentialStatus(statuses), cartTotal);
      return;
    }

    const cartTotalDollars = cartTotal / 100;
    const next = groupedStatuses.find((status) => !status.thresholdReached);
    const lastStatus = groupedStatuses[groupedStatuses.length - 1];

    let message = '';
    let isSuccess = false;
    let topUsesSuccessMessage = false;
    let messageOffer = next ? next.offer : lastStatus.offer;

    if (next) {
      const thresholdCents = Math.round(next.offer.cart_total_threshold * 100);
      const remaining = Math.max(thresholdCents - cartTotal, 0);
      const formatted = window.theme?.formatMoney
        ? window.theme.formatMoney(remaining, theme.moneyFormat)
        : `${(remaining / 100).toFixed(2)}`;
      message = (next.offer.pre_message || 'Add [amount] more to unlock next offer').replace('[amount]', formatted);
    } else {
      const hasLastOfferItems = Array.isArray(lastStatus.offerItems) && lastStatus.offerItems.length > 0;
      if (hasLastOfferItems && lastStatus.offer.success_message) {
        message = lastStatus.offer.success_message;
        isSuccess = true;
        topUsesSuccessMessage = true;
      } else {
        message = lastStatus.offer.post_message || lastStatus.offer.success_message || 'Offer unlocked';
      }
    }

    const displayMessageHtml = this.formatMessage(messageOffer, message);
    this.messageEl.innerHTML = displayMessageHtml;
    this.messageEl.classList.toggle('threshold-message--success', isSuccess);
    this.messageEl.style.display = 'block';
    const checkoutRawMessage = this.getCheckoutOnlyPrePostMessage(messageOffer, !next, cartTotal, topUsesSuccessMessage);
    const checkoutMessageHtml = this.formatMessage(messageOffer, checkoutRawMessage);
    this.renderCheckoutMessage(checkoutMessageHtml, false, Boolean(checkoutRawMessage));

    // Render offer sections exactly like multi-tier progress bar component.
    this.groupedOffersEl.innerHTML = '';
    this.groupedThresholdsEl.innerHTML = '';
    const count = groupedStatuses.length;

    groupedStatuses.forEach((status, index) => {
      const section = document.createElement('div');
      section.className = `threshold-grouped__section${status.thresholdReached ? ' active' : ''}`;
      section.style.width = `${100 / count}%`;

      const previousThreshold = index === 0 ? 0 : groupedStatuses[index - 1].offer.cart_total_threshold;
      const currentThreshold = status.offer.cart_total_threshold;
      const span = Math.max(currentThreshold - previousThreshold, 0);
      const amountInSegment = Math.max(0, Math.min(cartTotalDollars, currentThreshold) - previousThreshold);
      let fillPercent = 0;
      if (span > 0) {
        const ratio = amountInSegment / span;
        fillPercent = ratio >= 1 ? 100 : Math.min(ratio * 100, 50);
      }

      const fill = document.createElement('div');
      fill.className = 'threshold-grouped__section-fill';
      fill.style.width = `${fillPercent}%`;
      section.appendChild(fill);
      this.groupedOffersEl.appendChild(section);
    });

    groupedStatuses.forEach((status, index) => {
      const thresholdPercent = ((index + 0.5) / count) * 100;
      const threshold = document.createElement('div');
      threshold.className = `threshold-grouped__price-threshold${status.thresholdReached ? ' active' : ''}`;
      threshold.style.left = `${Math.min(Math.max(thresholdPercent, 0), 100)}%`;

      const marker = document.createElement('div');
      marker.className = 'threshold-grouped__marker';

      const price = document.createElement('div');
      price.className = 'threshold-grouped__price';
      const cents = Math.round(Number(status.offer.cart_total_threshold || 0) * 100);
      price.textContent = window.theme?.formatMoney ? window.theme.formatMoney(cents, theme.moneyFormat) : `$${(cents / 100).toFixed(2)}`;
      marker.appendChild(price);

      const iconWrap = document.createElement('div');
      iconWrap.className = 'threshold-grouped__icon-wrapper';
      if (status.offer.icon) {
        const img = document.createElement('img');
        img.src = status.offer.icon;
        img.alt = '';
        iconWrap.appendChild(img);
      } else {
        iconWrap.textContent = '🎁';
      }
      marker.appendChild(iconWrap);

      const title = document.createElement('div');
      title.className = 'threshold-grouped__title';
      const label =
        status.offer.title ||
        status.offer.product_options?.[0]?.product_title ||
        status.offer.selector_title ||
        status.offer.success_message ||
        status.offer.post_message ||
        'Offer';
      title.textContent = String(label).replace(/<[^>]*>/g, '').trim() || 'Offer';
      marker.appendChild(title);

      threshold.appendChild(marker);
      this.groupedThresholdsEl.appendChild(threshold);
    });

    if (this.progressEl) this.progressEl.style.display = 'none';
    if (this.groupedEl) this.groupedEl.style.display = 'block';

    // In grouped mode, still allow manual selection once an offer is unlocked.
    const manualUnlocked = statuses
      .filter((status) => status.thresholdReached && status.offer.offer_mode === 'manual_selection')
      .sort((a, b) => b.offer.cart_total_threshold - a.offer.cart_total_threshold)
      .find((status) => status.offerItems.length === 0);

    if (manualUnlocked) {
      this.renderSelector(manualUnlocked.offer);
    } else {
      this.hideSelector();
    }
  }

  getSequentialStatus(statuses) {
    const nextNotReached = statuses.find((status) => !status.thresholdReached);
    if (nextNotReached) return nextNotReached;
    return statuses[statuses.length - 1];
  }

  renderSequential(activeStatus, cartTotal) {
    const { offer, thresholdReached, offerItems } = activeStatus;
    const hasOfferItems = offerItems.length > 0;

    let messageHtml = '';
    let isSuccess = false;

    let topUsesSuccessMessage = false;
    if (thresholdReached) {
      if (offer.offer_mode === 'manual_selection' && !hasOfferItems) {
        messageHtml = this.formatMessage(offer, offer.post_message || 'Select your free gift');
        this.renderSelector(offer);
      } else if (hasOfferItems && offer.success_message) {
        messageHtml = this.formatMessage(offer, offer.success_message);
        isSuccess = true;
        topUsesSuccessMessage = true;
        this.hideSelector();
      } else {
        messageHtml = this.formatMessage(offer, offer.post_message || offer.success_message || 'Offer unlocked');
        isSuccess = true;
        this.hideSelector();
      }
    } else {
      const thresholdCents = Math.round(offer.cart_total_threshold * 100);
      const remaining = Math.max(thresholdCents - cartTotal, 0);
      const formatted = window.theme?.formatMoney
        ? window.theme.formatMoney(remaining, theme.moneyFormat)
        : `${(remaining / 100).toFixed(2)}`;
      const pre = (offer.pre_message || 'Add [amount] more to unlock this offer').replace('[amount]', formatted);
      messageHtml = this.formatMessage(offer, pre);
      this.hideSelector();
    }

    this.messageEl.innerHTML = messageHtml;
    this.messageEl.classList.toggle('threshold-message--success', isSuccess);
    this.messageEl.style.display = offer.show_messages ? 'block' : 'none';
    const checkoutRawMessage = this.getCheckoutOnlyPrePostMessage(offer, thresholdReached, cartTotal, topUsesSuccessMessage);
    const checkoutMessageHtml = this.formatMessage(offer, checkoutRawMessage);
    this.renderCheckoutMessage(checkoutMessageHtml, false, Boolean(offer.show_messages && checkoutRawMessage));

    if (offer.show_progress_bar && !thresholdReached) {
      const threshold = Math.max(Math.round(offer.cart_total_threshold * 100), 1);
      const percentage = Math.min((cartTotal / threshold) * 100, 100);
      this.progressFillEl.style.width = `${percentage}%`;
      this.progressEl.style.display = 'block';
    } else {
      this.progressEl.style.display = 'none';
    }
    if (this.groupedEl) this.groupedEl.style.display = 'none';
  }

  renderSelector(offer) {
    if (!this.selectorEl || offer.offer_mode !== 'manual_selection') return;

    const options = offer.product_options.length
      ? offer.product_options
      : offer.free_products.map((variantId) => ({
          variant_id: variantId,
          product_title: `Variant ${variantId}`,
          variant_title: '',
          image: ''
        }));

    if (!options.length) {
      this.hideSelector();
      return;
    }

    this.activeManualOfferId = offer.offer_id;
    this.selectorTitleEl.textContent = offer.selector_title || 'Select your free gift:';
    this.addButton.textContent = offer.add_button_text || 'Add Free Gift';
    this.addButton.dataset.defaultText = offer.add_button_text || 'Add Free Gift';
    this.addButton.dataset.loadingText = 'Adding...';
    this.isManualAddInProgress = false;
    this.setManualAddLoading(false);

    this.selectorOptionsEl.innerHTML = options
      .map((option, index) => {
        const image = option.image
          ? `<img src="${option.image}" alt="${(option.product_title || '').replace(/"/g, '&quot;')}" width="80" height="80" class="threshold-product-option__image" loading="lazy">`
          : '';

        return `
          <label class="threshold-product-option">
            <input type="radio" name="threshold-manual-option" value="${option.variant_id}" ${index === 0 ? 'checked' : ''}>
            ${image}
            <div class="threshold-product-option__info">
              <div class="threshold-product-option__title">${option.product_title || ''}</div>
              ${option.variant_title && option.variant_title !== 'Default Title' ? `<div class="threshold-product-option__variant">${option.variant_title}</div>` : ''}
            </div>
          </label>
        `;
      })
      .join('');

    this.selectorEl.style.display = 'block';
  }

  hideSelector() {
    this.activeManualOfferId = null;
    this.isManualAddInProgress = false;
    this.setManualAddLoading(false);
    if (this.selectorEl) this.selectorEl.style.display = 'none';
  }

  setManualAddLoading(isLoading) {
    if (!this.addButton) return;
    const defaultText = this.addButton.dataset.defaultText || this.addButton.textContent || 'Add Free Gift';
    const loadingText = this.addButton.dataset.loadingText || 'Adding...';

    this.addButton.classList.toggle('is-loading', isLoading);
    this.addButton.disabled = isLoading;
    this.addButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    this.addButton.textContent = isLoading ? loadingText : defaultText;
  }

  async handleManualAdd(event) {
    event.preventDefault();
    if (!this.activeManualOfferId) return;
    if (this.isManualAddInProgress) return;

    const offer = this.offers.find((item) => item.offer_id === this.activeManualOfferId);
    if (!offer) return;

    const selected = this.querySelector('input[name="threshold-manual-option"]:checked');
    if (!selected) return;

    const variantId = Number(selected.value || 0);
    if (!variantId) return;

    this.isManualAddInProgress = true;
    this.setManualAddLoading(true);

    const result = await this.addProductWithRetry(variantId, {
      [this.OFFER_PROPERTY_KEY]: offer.property_name
    });

    if (result.success) {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', {
        bubbles: true,
        detail: { source: 'threshold' }
      }));
      return;
    }

    this.isManualAddInProgress = false;
    this.setManualAddLoading(false);
  }

   updateContainerVisibility() {
    if (!this.container) return;
    // Hide the whole container (padding, margin, border) when no child is shown,
    // so an empty offer state doesn't leave a blank bar in the cart drawer.
    const children = [this.messageEl, this.progressEl, this.groupedEl, this.selectorEl];
    const hasVisible = children.some((el) => el && el.style.display !== 'none');
    this.container.style.display = hasVisible ? '' : 'none';
  }


  async process(cart) {
    if (this.isProcessing) return;
    this.isProcessing = true;

    try {
      this.currentCart = cart;
      this.currentCartTotal = this.calculateCartTotal(cart);

      const statuses = this.offers.map((offer) => {
        const offerItems = this.getOfferItems(cart, offer);
        const thresholdReached = this.currentCartTotal / 100 >= offer.cart_total_threshold;
        return { offer, offerItems, thresholdReached };
      });

      if (this.DEBUG) console.log('[ThresholdOffers][build-2026-04-13-3] cart_total', {
        cents: this.currentCartTotal,
        dollars: (this.currentCartTotal / 100).toFixed(2),
        thresholds: statuses.map((s) => ({
          property: s.offer.property_name,
          threshold: s.offer.cart_total_threshold,
          reached: s.thresholdReached,
          offerItems: s.offerItems.length
        }))
      });

      const mutated = await this.syncOfferProducts(cart, statuses);
      if (mutated) {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', {
          bubbles: true,
          detail: { source: 'threshold' }
        }));
        return;
      }

      const progressEnabledCount = this.offers.filter((offer) => offer.show_progress_bar === true).length;
      const useGrouped = this.offers.length > 1 && progressEnabledCount >= 2;

      if (useGrouped) {
        this.renderGrouped(statuses, this.currentCartTotal);
      } else {
        this.renderSequential(this.getSequentialStatus(statuses), this.currentCartTotal);
      }
 this.updateContainerVisibility();
      if (window.theme) {
        window.theme.cart = cart;
      }
    } catch (error) {
      console.error('[ThresholdOffers] process error', error);
    } finally {
      this.isProcessing = false;
    }
  }
}

if (!customElements.get('cart-threshold-offers')) {
  customElements.define('cart-threshold-offers', CartThresholdOffers);
}